import authStore from '@/firebase/auth';
import { allWorkspacesStateSingleton, V4UUID } from '@/firebase/database';
import { getTemplates, getTemplatesAsync } from '@/firebase/templateDb';
import {
  getStripeCustomerSubscription,
  getSubscriptionQuantity
} from '@/stripe/stripeSubscription';
import { defineStore } from 'pinia';
import Stripe from 'stripe';
import { v4 } from 'uuid';
import { LaunchFile } from '../controlCenterModule/components/robotConnection/loadLaunchFilesStrategy';
import { ConnectionDetails } from '../controlCenterModule/store';
import { PackageT } from '@/modules/nodesCanvasModule/components/storeClasses/packageT';

interface State {
  workspaces: Array<any>;
  nextWorkspaceId: number;
  templates: Array<ProjectTemplate>;
}

export interface ProjectTemplate {
  id: string;
  name: string;
  image?: string;
  connections: ConnectionDetails[];
  launchFiles: LaunchFile[];
  packages: PackageT[];
}

export const useProjectManagerStore = defineStore({
  id: 'projectManager',
  state: (): State => {
    return {
      workspaces: [],
      nextWorkspaceId: 1,
      templates: []
    };
  },
  actions: {
    loadWorkspaces(): void {
      if (localStorage.getItem('nextWorkspaceId') !== null) {
        this.nextWorkspaceId = Number(localStorage.nextWorkspaceId);
      } else this.nextWorkspaceId = 1;

      const workspaces: string | null = localStorage.getItem('workspaces');
      if (workspaces === null) {
        this.workspaces = [];
      } else {
        this.workspaces = JSON.parse(workspaces);
      }
      this.saveWorkspaces();
    },
    saveWorkspaces(): void {
      localStorage.workspaces = JSON.stringify(this.workspaces);
      localStorage.nextWorkspaceId = this.nextWorkspaceId;
    },
    removeWorkspace(id: V4UUID): void {
      this.workspaces = this.workspaces.filter((item) => item.id != id);
      this.saveWorkspaces();
    },
    duplicateWorkspace(id: V4UUID): void {
      let workspaceToDuplicate = {
        ...this.workspaces.find((workspace) => workspace.id === id)
      };
      workspaceToDuplicate.id = v4();
      this.workspaces.push(workspaceToDuplicate);
      this.saveWorkspaces();
    },
    canCreateWorkspace(
      userSubscription: Stripe.Subscription | undefined
    ): boolean {
      const workspacesInfo = allWorkspacesStateSingleton();

      if (!workspacesInfo.isSet) {
        return false;
      }
      if (!workspacesInfo.info || Object.keys(workspacesInfo.info).length < 1) {
        return true;
      }
      if (!userSubscription || !authStore || !authStore.email) {
        return false;
      }
      if (
        Object.keys(workspacesInfo.info).length <
        (getSubscriptionQuantity(userSubscription) || 1) * 5
      ) {
        return true;
      }
      return false;
    },
    async canCreateWorkspaceAsync(): Promise<boolean> {
      if (!authStore || !authStore.stripeId) return false;
      let userSubscription = await getStripeCustomerSubscription(
        authStore.stripeId
      );
      return this.canCreateWorkspace(userSubscription);
    },
    async loadTemplates(): Promise<void> {
      this.templates = [];
      const templates = await getTemplatesAsync();
      if (!templates) return;
      for (const template of Object.entries(templates)) {
        if (
          this.templates.filter(
            (currTemplate) => template[0] === currTemplate.id
          ).length > 0
        )
          continue;
        this.templates.push({
          id: template[0],
          name: template[1].name,
          image: template[1].image || undefined,
          connections: template[1].connections
            ? template[1].connections.map((con: any) =>
                ConnectionDetails.fromJSON(con)
              )
            : [],
          launchFiles: template[1].launchFiles,
          packages: template[1].packages
            ? template[1].packages.map((pkgData: any) =>
                PackageT.fromJSON(pkgData)
              )
            : []
        });
        this.templates = this.templates.sort((templateA, templateB) =>
          templateA.name.localeCompare(templateB.name)
        );
        console.log(this.templates);
      }
    },
    getTemplate(id: V4UUID) {
      return this.templates.find(
        (template: ProjectTemplate) => template.id === id
      );
    }
  }
});
